import type { Point } from './world-types.js';

export type ItemId = string;
/** Items and gold. Used for starting kits, rewards, caches and containers. */
export interface Contents { gold: number; items: Record<ItemId, number> }
/** A hero's pack. The same shape as Contents so transfers are plain arithmetic. */
export const EQUIPMENT_SLOTS = ['head', 'armor', 'weapon', 'shield', 'cloak', 'reserve', 'gear'] as const;
export type EquipmentSlotId = typeof EQUIPMENT_SLOTS[number];
export type EquippedItems = Partial<Record<EquipmentSlotId, ItemId | null>>;
export interface InventoryState extends Contents { equipment?: EquippedItems }
export interface HealingEffect { kind: 'heal'; count: number; sides: number; bonus: number }
/** Spent during a Field Rest to recover Wounds (Core Rules p. 37). */
export interface WoundCareEffect { kind: 'wound-care'; wounds: number }
export interface EquipmentEffect { kind: 'equipment'; slot: 'headwear' | 'armor' | 'weapon' | 'shield' | 'cape' | 'gear'; equipmentId: string;
  /** A one-handed melee weapon, which also fits the off hand (the `shield` slot) for dual wielding. */
  offHand?: boolean }
/** Opens a readable (`story-types.ts`) on parchment. Reading uses nothing up. */
export interface ReadableEffect { kind: 'readable'; readableId: string }
export type ItemEffect = HealingEffect | WoundCareEffect | EquipmentEffect | ReadableEffect;
export interface ItemDefinition {
  id: ItemId; name: string; description: string;
  effect: ItemEffect;
  /** Reference price in gold pieces; merchants set their own. */
  cost: number;
  /** How many of this item share one inventory slot. */
  stack: number;
}
export interface MerchantStock { itemId: ItemId; price: number; quantity: number }
export interface MerchantDefinition extends Point { id: string; name: string; stock: MerchantStock[] }
export interface CacheDefinition extends Point { id: string; name: string; contents: Contents }
export interface EncounterReward { encounterId: string; contents: Contents }
export interface ContainerState extends Point { id: string; name: string; gold: number; items: Record<ItemId, number>;
  /** A readable site: clicking it reads rather than loots. */
  readableId?: string }
export interface MerchantState { id: string; stock: Record<ItemId, number> }

export const BASE_INVENTORY_SLOTS = 10;
/** Maximum distance from a hero's base edge to a container for collecting. */
export const LOOT_REACH = 1.5;
/** Maximum distance from a hero's base edge to a merchant for trading. */
export const TRADE_REACH = 3;
/** Radius of a container's footprint, used for reach tests. */
export const CONTAINER_RADIUS = .5;

/** Prices and purses are in gold pieces, to the silver piece (10 sp = 1 gp): kept to tenths so sums never drift. */
export const coins = (gold: number) => Math.round(gold * 10) / 10;
/** `3 gp`, `2 sp` or `3 gp 4 sp`. */
export function formatCoins(gold: number): string {
  const silver = Math.round(coins(gold) * 10), whole = Math.floor(silver / 10), rest = silver % 10;
  return whole && rest ? `${whole} gp ${rest} sp` : rest ? `${rest} sp` : `${whole} gp`;
}
export const emptyContents = (): Contents => ({ gold: 0, items: {} });
export const copyContents = (contents: Contents): Contents => ({ gold: contents.gold, items: { ...contents.items } });
/** Slots a hero can fill with supplies: 10 + STR, never fewer than one. */
export const inventorySlots = (strength: number) => Math.max(1, BASE_INVENTORY_SLOTS + strength);
export function slotsUsed(items: Record<ItemId, number>, catalog: ReadonlyMap<ItemId, ItemDefinition>): number {
  let slots = 0;
  for (const [id, count] of Object.entries(items)) if (count > 0) slots += Math.ceil(count / Math.max(1, catalog.get(id)?.stack ?? 1));
  return slots;
}
/** How many more of an item fit in a pack, counting spare room in a partly filled slot. */
export function carryRoom(items: Record<ItemId, number>, strength: number, catalog: ReadonlyMap<ItemId, ItemDefinition>, itemId: ItemId): number {
  const item = catalog.get(itemId); if (!item) return 0;
  const stack = Math.max(1, item.stack), free = Math.max(0, inventorySlots(strength) - slotsUsed(items, catalog));
  return free * stack + (stack - (items[itemId] ?? 0) % stack) % stack;
}
/** Average HP restored, or 0 for items that do not heal HP. */
export function itemAverage(item: ItemDefinition): number { const { effect } = item; return effect.kind === 'heal' ? effect.count * (effect.sides + 1) / 2 + effect.bonus : 0; }
export const isPotion = (item: ItemDefinition): item is ItemDefinition & { effect: HealingEffect } => item.effect.kind === 'heal';
export const isReadable = (item: ItemDefinition): item is ItemDefinition & { effect: ReadableEffect } => item.effect.kind === 'readable';
export const isEquipment = (item: ItemDefinition): item is ItemDefinition & { effect: EquipmentEffect } => item.effect.kind === 'equipment';
/** The `shield` slot is the off hand: it holds a shield or a one-handed melee weapon. */
export function fitsEquipmentSlot(item: ItemDefinition, slot: EquipmentSlotId): boolean {
  if (slot === 'shield' && isEquipment(item) && item.effect.slot === 'weapon' && item.effect.offHand) return true;
  return isEquipment(item) && item.effect.slot === ({ head: 'headwear', armor: 'armor', weapon: 'weapon', reserve: 'weapon', shield: 'shield', cloak: 'cape', gear: 'gear' } as const)[slot];
}
export function describeEffect(item: ItemDefinition): string {
  const { effect } = item;
  return effect.kind === 'heal' ? `Restores ${effect.count}d${effect.sides}${effect.bonus ? `+${effect.bonus}` : ''} HP.` : effect.kind === 'readable' ? 'Can be read.' : effect.kind === 'equipment' ? `Equip in a ${effect.slot} slot.` : `Recovers ${effect.wounds} Wound${effect.wounds === 1 ? '' : 's'} during a Field Rest.`;
}
